import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { ApiError } from '../../api';
import { PieChart, User, LuSun, LuMoon } from '../../utils/icons';
import { FaKey, FaSignOutAlt } from 'react-icons/fa';
import { useTheme } from '../../context/ThemeContext';
import { palette } from '../../theme/palette';

const DashboardIcon = PieChart as React.ComponentType<{ className?: string }>;
const ProfileIcon = User as React.ComponentType<{ className?: string }>;
const SignInIcon = FaKey as React.ComponentType<{ className?: string }>;
const SignOutIcon = FaSignOutAlt as React.ComponentType<{ className?: string }>;
const SunIcon = LuSun as React.ComponentType<{ className?: string; size?: number }>;
const MoonIcon = LuMoon as React.ComponentType<{ className?: string; size?: number }>;

/** Extract a user-friendly message from login API errors. */
function getLoginErrorMessage(err: unknown): string {
  // ApiError from generated client (has status and body)
  if (err && typeof err === 'object' && 'status' in err && 'body' in err) {
    const apiErr = err as ApiError;
    const body = apiErr.body as { message?: string; errors?: Record<string, string[]>; detail?: string | unknown[] } | undefined;
    const status = apiErr.status;

    // Laravel 422: { message, errors: { email: ["..."], password: ["..."] } }
    if (body?.errors && typeof body.errors === 'object') {
      const first = Object.values(body.errors).flat()[0];
      if (typeof first === 'string') {
        const lower = first.toLowerCase();
        if (lower.includes('credential') || (lower.includes('invalid') && (lower.includes('email') || lower.includes('password'))))
          return 'Invalid email or password. Please try again.';
        if (lower.includes('required') || lower.includes('field is required'))
          return 'Please enter your email and password.';
        return first;
      }
    }

    const fromBody =
      (typeof body?.message === 'string' && body.message) ||
      (typeof (body as { detail?: string })?.detail === 'string' && (body as { detail: string }).detail) ||
      (typeof (body as { error?: string })?.error === 'string' && (body as { error: string }).error);

    if (fromBody) {
      const lower = fromBody.toLowerCase();
      if (lower.includes('account') && (lower.includes('not exist') || lower.includes('not found') || lower.includes('doesn\'t exist')))
        return 'Account does not exist. Please check your email or sign up.';
      if (lower.includes('invalid') && (lower.includes('credential') || lower.includes('password') || lower.includes('email')))
        return 'Invalid email or password. Please try again.';
      if (lower.includes('inactive') || lower.includes('disabled') || lower.includes('suspended'))
        return 'This account is inactive. Please contact support.';
      return fromBody;
    }

    // Validation errors (e.g. 422 with detail as array)
    if (body != null && Array.isArray(body.detail) && body.detail.length > 0) {
      const first = body.detail[0];
      const msg = typeof (first as { msg?: string })?.msg === 'string' ? (first as { msg: string }).msg : (typeof first === 'string' ? first : null);
      if (msg) return msg;
    }

    // Status-based fallbacks when body has no usable message
    switch (status) {
      case 401:
        return 'Invalid email or password. Please check your credentials and try again.';
      case 403:
        return 'This account is inactive. Please contact support.';
      case 404:
        return 'Account does not exist. Please check your email or sign up.';
      case 422:
        return 'Please enter a valid email and password.';
      case 500:
      case 502:
      case 503:
        return 'Something went wrong. Please try again later.';
      default:
        return apiErr.message || 'Invalid email or password. Please try again.';
    }
  }

  // Axios-style (response.data)
  const ax = err as { response?: { data?: { message?: string; detail?: string }; status?: number } };
  if (ax?.response?.data) {
    const msg = ax.response.data.message || ax.response.data.detail;
    if (typeof msg === 'string') return msg;
    if (ax.response.status === 401) return 'Invalid email or password. Please check your credentials and try again.';
    if (ax.response.status === 404) return 'Account does not exist. Please check your email or sign up.';
  }

  return 'Invalid email or password. Please try again.';
}

const SignInPage: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const { login, logout, isLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard';
  const isOnSignInPage = location.pathname === '/sign-in' || location.pathname === '/login';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();
    if (!trimmedEmail || !trimmedPassword) {
      toast.error('Please enter your email and password.');
      return;
    }

    try {
      await login(trimmedEmail, trimmedPassword);
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(getLoginErrorMessage(err));
    }
  };

  const logoPath = theme === 'dark' ? '/light%20green.png' : '/green.png';
  const welcomeColor = theme === 'dark' ? palette.primaryLight : palette.primary;
  const signInButtonBg = theme === 'dark' ? palette.primaryLight : palette.primary;
  const signInButtonTextClass = theme === 'dark' ? 'text-primary-900' : 'text-white';

  return (
    <div className="m-0 font-sans antialiased font-normal bg-white dark:bg-stone-900 text-start text-base leading-default text-slate-500 dark:text-slate-200 min-h-screen">
      {/* Navbar */}
      <div className="container sticky top-0 z-sticky">
        <div className="flex flex-wrap -mx-3">
          <div className="w-full max-w-full px-3 flex-0">
            <nav className="absolute top-0 left-0 right-0 z-30 flex flex-wrap items-center px-4 py-2 mx-6 my-4 shadow-soft-2xl rounded-blur bg-white/80 dark:bg-stone-800/60 backdrop-blur-2xl backdrop-saturate-200 ring-1 ring-black/5 dark:ring-white/10 lg:flex-nowrap lg:justify-start">
              <div className="flex items-center justify-between w-full p-0 pl-6 mx-auto flex-wrap-inherit">
                <Link to="/" className="flex items-center py-2.375 text-sm mr-4 ml-4 whitespace-nowrap font-bold text-slate-700 dark:text-slate-100 lg:ml-0">
                  <img src={logoPath} alt="ClickLaw" className="h-14 w-auto mr-2" />
                  {/* <span>ClickLaw</span> */}
                </Link>
                <button
                  className="px-3 py-1 ml-2 leading-none transition-all bg-transparent border border-transparent border-solid rounded-lg shadow-none cursor-pointer text-lg ease-soft-in-out lg:hidden"
                  type="button"
                  aria-controls="navigation"
                  aria-expanded="false"
                  aria-label="Toggle navigation"
                >
                  <span className="inline-block mt-2 align-middle bg-center bg-no-repeat bg-cover w-6 h-6 bg-none">
                    <span className="w-5.5 rounded-xs relative my-0 mx-auto block h-px bg-gray-600 dark:bg-slate-200 transition-all duration-300"></span>
                    <span className="w-5.5 rounded-xs mt-1.75 relative my-0 mx-auto block h-px bg-gray-600 dark:bg-slate-200 transition-all duration-300"></span>
                    <span className="w-5.5 rounded-xs mt-1.75 relative my-0 mx-auto block h-px bg-gray-600 dark:bg-slate-200 transition-all duration-300"></span>
                  </span>
                </button>
                <div className="items-center flex-grow overflow-hidden transition-all duration-500 ease-soft lg-max:max-h-0 basis-full lg:flex lg:basis-auto">
                  <ul className="flex flex-col pl-0 mx-auto mb-0 list-none lg:flex-row xl:ml-auto">
                    {isAuthenticated && (
                      <li>
                        <Link
                          to="/dashboard"
                          className="flex items-center px-4 py-2 mr-2 font-normal transition-all lg-max:opacity-0 duration-250 ease-soft-in-out text-sm text-slate-700 dark:text-slate-200 lg:px-2"
                        >
                          <DashboardIcon className="mr-1 opacity-60" />
                          Dashboard
                        </Link>
                      </li>
                    )}
                    {isAuthenticated && (
                      <li>
                        <Link
                          to="/profile"
                          className="block px-4 py-2 mr-2 font-normal transition-all lg-max:opacity-0 duration-250 ease-soft-in-out text-sm text-slate-700 dark:text-slate-200 lg:px-2"
                        >
                          <ProfileIcon className="mr-1 opacity-60" />
                          Profile
                        </Link>
                      </li>
                    )}
                    {(isAuthenticated || !isOnSignInPage) && (
                      <li>
                        {isAuthenticated ? (
                          <button
                            type="button"
                            onClick={() => logout()}
                            className="flex items-center px-4 py-2 mr-2 font-normal transition-all lg-max:opacity-0 duration-250 ease-soft-in-out text-sm text-slate-700 dark:text-slate-200 lg:px-2 border-0 bg-transparent cursor-pointer w-full lg:w-auto text-left"
                          >
                            <SignOutIcon className="mr-1 opacity-60" />
                            Sign Out
                          </button>
                        ) : (
                          <Link
                            to="/sign-in"
                            className="block px-4 py-2 mr-2 font-normal transition-all lg-max:opacity-0 duration-250 ease-soft-in-out text-sm text-slate-700 dark:text-slate-200 lg:px-2"
                          >
                            <SignInIcon className="mr-1 opacity-60" />
                            Sign In
                          </Link>
                        )}
                      </li>
                    )}
                  </ul>
                  <div className="flex items-center ml-4">
                    <button
                      onClick={toggleTheme}
                      className="block px-3 py-2 font-semibold transition-all ease-soft-in-out text-sm text-slate-700 dark:text-slate-200 hover:opacity-75"
                      title="Toggle theme"
                      aria-label="Toggle theme"
                    >
                      {theme === 'dark' ? (
                        <SunIcon className="inline" size={20} />
                      ) : (
                        <MoonIcon className="inline" size={20} />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </nav>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="mt-0 transition-all duration-200 ease-soft-in-out">
        <section>
          <div className="relative flex items-center p-0 overflow-hidden bg-center bg-cover min-h-75-screen">
            <div className="container z-10">
              <div className="flex flex-wrap mt-0 -mx-3">
                <div className="flex flex-col w-full max-w-full px-3 mx-auto md:flex-0 shrink-0 md:w-6/12 lg:w-5/12 xl:w-4/12">
                  <div className="relative flex flex-col min-w-0 mt-32 break-words bg-transparent border-0 shadow-none rounded-2xl bg-clip-border">
                    <div className="p-6 pb-0 mb-0 bg-transparent border-b-0 rounded-t-2xl">
                      <h3 className="relative z-10 font-bold" style={{ color: welcomeColor }}>
                        Welcome back
                      </h3>
                      <p className="mb-0">Enter your email and password to sign in</p>
                    </div>
                    <div className="flex-auto p-6">
                      <form role="form" onSubmit={handleSubmit}>
                        <label className="mb-2 ml-1 font-bold text-xs text-slate-700 dark:text-slate-200">Email</label>
                        <div className="mb-4">
                          <input
                            type="email"
                            autoComplete="email"
                            className="focus:shadow-soft-primary-outline text-sm leading-5.6 ease-soft block w-full appearance-none rounded-lg border border-solid border-gray-300 dark:border-white/10 bg-white dark:bg-stone-800/60 bg-clip-padding px-3 py-2 font-normal text-gray-700 dark:text-slate-100 transition-all focus:outline-none focus:transition-shadow"
                            style={{ '--tw-ring-color': palette.primary } as React.CSSProperties & { '--tw-ring-color'?: string }}
                            onFocus={(e) => (e.target as HTMLInputElement).style.borderColor = palette.primary}
                            onBlur={(e) => (e.target as HTMLInputElement).style.borderColor = '#d1d5db'}
                            placeholder="Email"
                            aria-label="Email"
                            aria-describedby="email-addon"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                          />
                        </div>
                        <label className="mb-2 ml-1 font-bold text-xs text-slate-700 dark:text-slate-200">Password</label>
                        <div className="mb-4">
                          <input
                            type="password"
                            autoComplete="current-password"
                            className="focus:shadow-soft-primary-outline text-sm leading-5.6 ease-soft block w-full appearance-none rounded-lg border border-solid border-gray-300 dark:border-white/10 bg-white dark:bg-stone-800/60 bg-clip-padding px-3 py-2 font-normal text-gray-700 dark:text-slate-100 transition-all focus:outline-none focus:transition-shadow"
                            style={{ '--tw-ring-color': palette.primary } as React.CSSProperties & { '--tw-ring-color'?: string }}
                            onFocus={(e) => (e.target as HTMLInputElement).style.borderColor = palette.primary}
                            onBlur={(e) => (e.target as HTMLInputElement).style.borderColor = '#d1d5db'}
                            placeholder="Password"
                            aria-label="Password"
                            aria-describedby="password-addon"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                          />
                        </div>
                        <div className="flex items-center justify-between min-h-6 mb-0.5">
                          <div className="pl-12">
                            <input
                              id="rememberMe"
                              className="mt-0.54 rounded-10 duration-250 ease-soft-in-out after:rounded-circle after:shadow-soft-2xl after:duration-250 checked:after:translate-x-5.25 h-5 relative float-left -ml-12 w-10 cursor-pointer appearance-none border border-solid border-gray-200 bg-slate-800/10 bg-none bg-contain bg-left bg-no-repeat align-top transition-all after:absolute after:top-px after:h-4 after:w-4 after:translate-x-px after:bg-white after:content-[''] checked:border-slate-800/95 checked:bg-slate-800/95 checked:bg-none checked:bg-right"
                              type="checkbox"
                              checked={rememberMe}
                              onChange={(e) => setRememberMe(e.target.checked)}
                            />
                            <label className="mb-2 ml-1 font-normal cursor-pointer select-none text-sm text-slate-700 dark:text-slate-200" htmlFor="rememberMe">
                              Remember me
                            </label>
                          </div>
                          <Link to="/forgot-password" className="text-sm font-normal text-primary dark:text-primary-light hover:underline">
                            Forgot password?
                          </Link>
                        </div>
                        <div className="text-center">
                          <button
                            type="submit"
                            disabled={isLoading}
                            className={`inline-block w-full px-6 py-3 mt-6 mb-0 font-bold text-center uppercase align-middle transition-all bg-transparent border-0 rounded-lg cursor-pointer shadow-soft-md bg-x-25 bg-150 leading-pro text-xs ease-soft-in tracking-tight-soft hover:scale-102 hover:shadow-soft-xs active:opacity-85 disabled:opacity-50 disabled:cursor-not-allowed ${signInButtonTextClass}`}
                            style={{ backgroundColor: signInButtonBg }}
                          >
                            {isLoading ? 'Signing in...' : 'Sign in'}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
                <div className="w-full max-w-full px-3 lg:flex-0 shrink-0 md:w-6/12">
                  <div className="absolute top-0 hidden w-3/5 h-full -mr-32 overflow-hidden -skew-x-10 -right-40 rounded-bl-xl md:block">
                    <div
                      className="absolute inset-x-0 top-0 z-0 h-full -ml-16 bg-cover skew-x-10"
                      style={{ backgroundImage: "url('/view-3d-justice-scales.jpg')" }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-12">
        <div className="container">
          <div className="flex flex-wrap -mx-3">
            <div className="flex-shrink-0 w-full max-w-full mx-auto mb-6 text-center lg:flex-0 lg:w-8/12">
            
            </div>
            <div className="flex-shrink-0 w-full max-w-full mx-auto mt-2 mb-6 text-center lg:flex-0 lg:w-8/12">
              <a
                href="https://www.tiktok.com/@clicklaww"
                target="_blank"
                rel="noreferrer"
                className="mr-6 text-slate-400"
                aria-label="TikTok"
              >
                <span className="text-lg fab fa-tiktok" aria-hidden />
              </a>
              <a
                href="https://www.linkedin.com/company/clicklaww"
                target="_blank"
                rel="noreferrer"
                className="mr-6 text-slate-400"
                aria-label="LinkedIn"
              >
                <span className="text-lg fab fa-linkedin" aria-hidden />
              </a>
              <a
                href="https://clicklaw.tech/"
                target="_blank"
                rel="noreferrer"
                className="mr-6 text-slate-400"
                aria-label="Website"
              >
                <span className="text-lg fas fa-globe" aria-hidden />
              </a>
              <a
                href="https://web.facebook.com/profile.php?id=61580800284878"
                target="_blank"
                rel="noreferrer"
                className="mr-6 text-slate-400"
                aria-label="Facebook"
              >
                <span className="text-lg fab fa-facebook" aria-hidden />
              </a>
            </div>
          </div>
          <div className="flex flex-wrap -mx-3">
            <div className="w-8/12 max-w-full px-3 mx-auto mt-1 text-center flex-0">
              <p className="mb-0 text-slate-400">
                Copyright © {new Date().getFullYear()} ClickLaw.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SignInPage;
